import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, runWithAuditReason } from '../../api/client';
import '../backlog/backlog.css';
import { fmtLocalDate } from '../../utils/datetime';
import { navMeta, pageGroups } from '../../layouts/navData';
import { LoaderSymbol } from '../../components/loading';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar } from '../../components/AppToolbar';
import { useNotify } from '../../components/notifications';
import { useConfirm, useReasonGate } from '../../components/confirm';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface CalendarEvent {
  jobId:        string;
  reminderId:   string;
  eventDate:    string;   // "YYYY-MM-DD"
  projectName:  string;
  client:       string;
  createdDate:  string | null;
  updatedDate:  string | null;
  status:       string;
}

interface JobOption {
  jobOid:      string;
  jobId:       string;
  eventDate:   string | null;
  projectName: string;
  client:      string;
}

type ViewMode = 'month' | 'timeline';

// ─────────────────────────────────────────────────────────────────────────────
// Color legend — matches v5 HTML exactly
// token-exempt: calendar task-category legend palette — distinct per-category colours, data-viz (no --ai-* equivalents)
// ─────────────────────────────────────────────────────────────────────────────
const LEGEND: { label: string; color: string }[] = [
  { label: 'Blended Run 1', color: '#28a745' },
  { label: 'Blended Run 2', color: '#17a2b8' },
  { label: 'Blended Run 3', color: '#ffc107' },
  { label: 'Blended Run 4', color: '#dc3545' },
];

function eventColor(projectName: string): string {
  const n = projectName.toLowerCase();
  if (n.includes('blended run 1') || n === 'blended run 1') return '#28a745';
  if (n.includes('blended run 2') || n === 'blended run 2') return '#17a2b8';
  if (n.includes('blended run 3') || n === 'blended run 3') return '#ffc107';
  if (n.includes('blended run 4') || n === 'blended run 4') return '#dc3545';
  if (n.includes('blended run'))  return '#6c757d';
  if (n.includes('skipped'))      return '#adb5bd';
  if (n.startsWith('c'))          return '#6f42c1';
  if (n.startsWith('ne') || n.startsWith('nw') || n.startsWith('se') || n.startsWith('sw')) return '#fd7e14';
  if (n.startsWith('well'))       return '#20c997';
  if (n.startsWith('wc'))         return '#0dcaf0';
  return '#6c757d';
}

// ─────────────────────────────────────────────────────────────────────────────
// Date helpers
// ─────────────────────────────────────────────────────────────────────────────
const DAYS   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function monthLabel(d: Date) { return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; }
function sameDay(a: Date, b: Date) {
  return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
}
function addMonths(d: Date, n: number): Date {
  const r = new Date(d); r.setDate(1); r.setMonth(r.getMonth()+n); return r;
}

// Build the 6-week grid for a month view
function buildMonthGrid(year: number, month: number): Date[][] {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());  // go back to Sunday
  const grid: Date[][] = [];
  const cur = new Date(start);
  for (let w = 0; w < 6; w++) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) { week.push(new Date(cur)); cur.setDate(cur.getDate()+1); }
    grid.push(week);
  }
  return grid;
}

// ─────────────────────────────────────────────────────────────────────────────
// Event chip — clicking selects the underlying reminder (enables Edit/Delete)
// ─────────────────────────────────────────────────────────────────────────────
function EventChip({ event, selected, onSelect }: {
  event: CalendarEvent; selected: boolean; onSelect: (event: CalendarEvent) => void;
}) {
  const color = eventColor(event.projectName);
  const isPending = event.status === 'Pending Sampling';
  return (
    <div
      className={`sc-event-chip${selected ? ' sc-event-chip-selected' : ''}`}
      style={{ background: color }}
      onClick={(e) => { e.stopPropagation(); onSelect(event); }}
      title={`${event.jobId} · ${event.client}`}
    >
      <span>{event.projectName}</span>
      {isPending && <span className="sc-event-dot" title="Pending Sampling" />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Day detail modal — matches GoLive popup: shows PENDING SAMPLING only
// Columns: Project Name | Event Date | Updated Date
// ─────────────────────────────────────────────────────────────────────────────
// Render via the shared §14b.6 helper (local tz, MM/DD/YYYY); keep the '—'
// placeholder for empty/unparseable data values.  (The calendar-navigation
// labels below intentionally keep weekday/long-month names — FLAGGED.)
function fmtMMDDYYYY(v: string | null | undefined): string {
  return v ? (fmtLocalDate(v) || '—') : '—';
}

function DayModal({ date, events, onClose }: { date: Date; events: CalendarEvent[]; onClose: () => void }) {
  // Permitted exception (§18.6 / C2): calendar-day HEADER label (weekday + long
  // month), not a data value — data values below go through fmtLocal*.
  const label = date.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' });
  // GoLive shows only Pending Sampling items in the popup (16 of 56 on May 1)
  const pending = events.filter(e => e.status === 'Pending Sampling');
  const [search, setSearch] = React.useState('');
  const filtered = search.trim()
    ? pending.filter(e => e.projectName.toLowerCase().includes(search.toLowerCase())
        || e.jobId.toLowerCase().includes(search.toLowerCase()))
    : pending;

  return (
    <div className="sc-modal-backdrop" onClick={onClose}>
      <div className="sc-modal sc-modal-wide" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sc-modal-hdr">
          <i className="ti ti-calendar-stats" style={{ marginRight: 6, color: 'var(--brand-accent, var(--ai-orange))' }} />
          <span>Scheduled Task Tracking</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="sc-modal-search">
              <i className="ti ti-search" />
              <input
                type="text" placeholder="Text to search…"
                value={search} onChange={e => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <button className="sc-modal-close" onClick={onClose}><i className="ti ti-x" /></button>
          </div>
        </div>
        {/* Sub-title */}
        <div className="sc-modal-sub">{label}</div>
        {/* List */}
        <div className="sc-modal-body">
          {filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:24, color:'var(--ai-muted)' }}>No pending items</div>
          ) : filtered.map((ev, i) => (
            <div key={i} className="sc-modal-row">
              <span className="sc-modal-dot" style={{ background: eventColor(ev.projectName) }} />
              <div className="sc-modal-info">
                <div className="sc-modal-name">{ev.projectName}</div>
                <div className="sc-modal-meta">
                  {ev.jobId} · {ev.client} · Updated: {fmtMMDDYYYY(ev.updatedDate)}
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Footer */}
        <div className="sc-modal-footer">
          <span className="sc-modal-pg-info">
            Page 1 of 1 ({filtered.length} item{filtered.length !== 1 ? 's' : ''})
          </span>
          <span className="sc-modal-count" style={{ marginLeft: 'auto' }}>
            {pending.length} pending · {events.length - pending.length} submitted
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Day cell (month grid)
// ─────────────────────────────────────────────────────────────────────────────
const MAX_VISIBLE = 3;
function DayCell({
  date, isCurrentMonth, isToday, events, selectedId, onSelect, onShowMore
}: {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
  selectedId: string | null;
  onSelect: (event: CalendarEvent) => void;
  onShowMore: (date: Date, events: CalendarEvent[]) => void;
}) {
  const visible = events.slice(0, MAX_VISIBLE);
  const extra   = events.length - MAX_VISIBLE;
  return (
    <div className={`sc-day${isCurrentMonth ? '' : ' sc-other-month'}${isToday ? ' sc-today' : ''}`}>
      <div className="sc-day-num">{date.getDate()}</div>
      <div className="sc-day-events">
        {visible.map((ev, i) => (
          <EventChip key={i} event={ev} selected={ev.reminderId === selectedId} onSelect={onSelect} />
        ))}
        {extra > 0 && (
          <button className="sc-show-more" onClick={() => onShowMore(date, events)}>
            Show more
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Timeline view — list of events ordered by date
// ─────────────────────────────────────────────────────────────────────────────
function TimelineView({ eventsByDate, selectedId, onSelect }: {
  eventsByDate: Map<string, CalendarEvent[]>;
  selectedId: string | null;
  onSelect: (event: CalendarEvent) => void;
}) {
  // Timeline shows Pending Sampling only — matches GoLive behaviour
  const entries = Array.from(eventsByDate.entries())
    .map(([d, evs]) => [d, evs.filter(e => e.status === 'Pending Sampling')] as [string, CalendarEvent[]])
    .filter(([, evs]) => evs.length > 0)
    .sort(([a],[b]) => a.localeCompare(b));

  if (entries.length === 0) return (
    <div className="bl-empty" style={{ margin: 40 }}><i className="ti ti-calendar-off" /> No pending sampling events</div>
  );
  return (
    <div className="sc-timeline">
      {entries.map(([dateStr, evs]) => {
        const d = new Date(dateStr + 'T00:00:00');
        return (
          <div key={dateStr} className="sc-tl-group">
            <div className="sc-tl-date">
              {/* Permitted exception (§18.6 / C2): timeline GROUP-HEADER label
                  (calendar navigation), not a data value. */}
              {d.toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric', year:'numeric' })}
              <span className="sc-tl-badge">{evs.length}</span>
            </div>
            <div className="sc-tl-events">
              {evs.map((ev, i) => (
                <div
                  key={i}
                  className={`sc-tl-row${ev.reminderId === selectedId ? ' sc-tl-row-selected' : ''}`}
                  onClick={() => onSelect(ev)}
                >
                  <span className="sc-tl-dot" style={{ background: eventColor(ev.projectName) }} />
                  <span className="sc-tl-name">{ev.projectName}</span>
                  <span className="sc-tl-meta">{ev.jobId} · {ev.client}</span>
                  <span className="bl-pill warn" style={{ fontSize: 10, padding: '1px 7px' }}>
                    Pending Sampling
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// New / Edit Reminder Activity dialog — searchable job picker (ALPACA
// "New Reminder Activity" parity).  dbo.ReminderActivity has no fields of
// its own besides the linked job, so New/Edit both boil down to picking one.
// ─────────────────────────────────────────────────────────────────────────────
function ReminderDialog({ mode, initial, onClose, onSaved }: {
  mode: 'new' | 'edit';
  initial: CalendarEvent | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const notify = useNotify();
  const [query,    setQuery]    = useState('');
  const [options,  setOptions]  = useState<JobOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked,   setPicked]   = useState<JobOption | null>(null);
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get<{ success: boolean; data: JobOption[] }>(
          '/api/v1/scheduled-tasks/jobs', { params: query ? { q: query } : {} });
        if (!cancelled && res.data.success) setOptions(res.data.data);
      } catch { /* leave prior options on a transient search error */ }
      finally { if (!cancelled) setSearching(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const save = async () => {
    if (!picked) { notify.warning('Select a job first.'); return; }
    setSaving(true);
    try {
      if (mode === 'new') {
        await api.post('/api/v1/scheduled-tasks/reminders', { jobId: picked.jobOid });
      } else if (initial) {
        await api.put(`/api/v1/scheduled-tasks/reminders/${initial.reminderId}`, { newJobId: picked.jobOid });
      }
      notify.success('Saved successfully.');
      onSaved();
    } catch (e: unknown) {
      notify.error(e instanceof Error ? e.message : 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="sc-modal-backdrop" onClick={onClose}>
      <div className="sc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sc-modal-hdr">
          <i className="ti ti-calendar-plus" style={{ marginRight: 6, color: 'var(--brand-accent, var(--ai-orange))' }} />
          <span>{mode === 'new' ? 'New Reminder Activity' : 'Edit Reminder Activity'}</span>
          <button className="sc-modal-close" style={{ marginLeft: 'auto' }} onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div className="sc-modal-body" style={{ padding: '12px 16px' }}>
          <div className="sc-modal-search" style={{ width: '100%', marginBottom: 10 }}>
            <i className="ti ti-search" />
            <input
              type="text" placeholder="Search by JobID / Project / Client…" style={{ width: '100%' }}
              value={query} onChange={(e) => { setQuery(e.target.value); setPicked(null); }} autoFocus
            />
          </div>
          {searching ? (
            <div className="bl-loading" style={{ margin: 12 }}><LoaderSymbol size={14} /> Searching…</div>
          ) : options.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 16, color: 'var(--ai-muted)', fontSize: 12 }}>No matching jobs</div>
          ) : options.map((o) => (
            <div
              key={o.jobOid}
              className={`sc-modal-row${picked?.jobOid === o.jobOid ? ' sc-modal-row-selected' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setPicked(o)}
            >
              <span className="sc-modal-dot" style={{ background: eventColor(o.projectName) }} />
              <div className="sc-modal-info">
                <div className="sc-modal-name">{o.jobId} · {o.client}</div>
                <div className="sc-modal-meta">{o.projectName} — {fmtMMDDYYYY(o.eventDate)}</div>
              </div>
              {picked?.jobOid === o.jobOid && <i className="ti ti-check" style={{ color: 'var(--brand-accent, var(--ai-orange))' }} />}
            </div>
          ))}
        </div>
        <div className="sc-modal-footer">
          <span className="sc-modal-pg-info">
            {picked ? `Selected: ${picked.jobId}` : 'No job selected'}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="ca-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={saving || !picked}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function ScheduledTaskPage({ pageId = 'schedtask' }: { pageId?: string }) {
  // Title + breadcrumb group come from the nav entry for THIS id, so the same
  // calendar shows "Task Calendar" under "Task Tracker" when opened via taskcal,
  // and "Scheduled Task Tracking" under "Sample Scheduling" via schedtask (§18.6).
  const navTitle = navMeta(pageId)?.label ?? 'Scheduled Task Tracking';
  const navGroup = pageGroups[pageId] ?? 'Sample Scheduling';
  const notify = useNotify();
  const { confirm } = useConfirm();
  const gateReason = useReasonGate();
  const today  = useMemo(() => new Date(), []);
  const [anchor,    setAnchor]    = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [viewMode,  setViewMode]  = useState<ViewMode>('month');
  const [events,    setEvents]    = useState<CalendarEvent[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [loadMs,    setLoadMs]    = useState<number | null>(null);
  const [error,     setError]     = useState<string>();
  const [modal,     setModal]     = useState<{ date: Date; events: CalendarEvent[] } | null>(null);
  const [search,    setSearch]    = useState('');
  const [selected,  setSelected]  = useState<CalendarEvent | null>(null);
  const [dialog,    setDialog]    = useState<'new' | 'edit' | null>(null);
  const [deleting,  setDeleting]  = useState(false);

  // Compute fetch window based on view
  const { startDate, endDate } = useMemo(() => {
    if (viewMode === 'month') {
      // full 6-week grid window
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const start = new Date(first); start.setDate(1 - first.getDay());
      const end   = new Date(start); end.setDate(end.getDate() + 41);
      return { startDate: toYMD(start), endDate: toYMD(end) };
    }
    // timeline — full dataset
    return { startDate: undefined, endDate: undefined };
  }, [viewMode, anchor]);

  const loadEvents = useCallback(async () => {
    setLoading(true); setError(undefined);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (startDate) params.startDate = startDate;
      if (endDate)   params.endDate   = endDate;
      const res = await api.get<{ success: boolean; data: CalendarEvent[] }>(
        '/api/v1/scheduled-tasks/events', { params });
      if (res.data.success) setEvents(res.data.data);
      else setError('Failed to load events.');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally { setLoadMs(performance.now() - t0); setLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Text search (standard list-search-row pattern, §18.6) — filters the
  // already-loaded events by JobID / Project / Client.
  const visibleEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter(ev =>
      ev.jobId.toLowerCase().includes(q)
      || ev.projectName.toLowerCase().includes(q)
      || ev.client.toLowerCase().includes(q));
  }, [events, search]);

  // Group events by date string
  const eventsByDate = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const ev of visibleEvents) {
      const key = ev.eventDate.substring(0, 10);
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(ev);
    }
    return m;
  }, [visibleEvents]);

  // Navigation
  function goBack() {
    if (viewMode === 'month') setAnchor(a => addMonths(a, -1));
  }
  function goForward() {
    if (viewMode === 'month') setAnchor(a => addMonths(a, 1));
  }
  function goToday() {
    setAnchor(new Date(today.getFullYear(), today.getMonth(), 1));
  }

  function selectEvent(ev: CalendarEvent) {
    setSelected(prev => prev?.reminderId === ev.reminderId ? null : ev);
  }

  async function deleteSelected() {
    if (!selected) return;
    if (!await confirm({
      title: 'Delete', tone: 'danger', confirmLabel: 'Delete',
      message: `Delete the reminder for job ${selected.jobId} (${selected.client})?`,
    })) return;
    const reason = await gateReason('Task Calendar', 'delete');
    if (reason === null) return;
    setDeleting(true);
    try {
      await runWithAuditReason(reason, () =>
        api.delete(`/api/v1/scheduled-tasks/reminders/${selected.reminderId}`));
      notify.success('Deleted.');
      setSelected(null);
      await loadEvents();
    } catch (e: unknown) {
      notify.error(e instanceof Error ? e.message : 'Delete failed.');
    } finally { setDeleting(false); }
  }

  // Permitted exception (§18.6 / C2): the label below is a calendar-NAVIGATION
  // header (month banner), not a data value — no AM/PM, no time component;
  // data values render via the shared fmtLocal* helpers.
  function headerLabel(): string {
    if (viewMode === 'month') return monthLabel(anchor);
    return 'All Events';
  }

  // Month grid
  const monthGrid = useMemo(() =>
    buildMonthGrid(anchor.getFullYear(), anchor.getMonth()), [anchor]);

  return (
    <div className="bl-page">

      {/* ── Fixed top ── */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>{navGroup}</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{navTitle}</span>
          </div>
          {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
              via navMeta + the TOP load-time chip. */}
          <div className="dp-title-row">
            <FormTitle pageId={pageId} title={navTitle} loadMs={loadMs} loading={loading} auditFormName="Task Calendar" />
          </div>
        </div>
        {error && (
          <div className="bl-err">
            <i className="ti ti-alert-circle" style={{ fontSize:14, verticalAlign:'middle', marginRight:5 }} />
            {error}
          </div>
        )}
      </div>

      {/* ── Calendar area ── */}
      <div className="bl-grid-area" style={{ paddingTop: 0 }}>
        {/* ── Action toolbar + search — standard AppToolbar (§18.6, CLAUDE.md
            standing rule 7), matching ALPACA's ReminderActivity_ListView:
            New Reminder Activity · Edit · Delete · Refresh, + text search. ── */}
        <AppToolbar
          newLabel="New Reminder Activity"
          onNew={() => setDialog('new')}
          onEdit={() => setDialog('edit')} editDisabled={!selected}
          onDelete={() => void deleteSelected()} deleteDisabled={!selected || deleting}
          onRefresh={() => void loadEvents()} refreshing={loading}
        />
        <div className="list-search-row">
          <div className="list-search"><span className="tb-i">🔍</span>
            <input placeholder="Text to search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        <div className="bl-card bl-card-flex" style={{ padding: 0 }}>

          {/* ── Toolbar — matches v5 cal-toolbar exactly ── */}
          <div className="cal-toolbar">
            <div className="cal-nav">
              <button className="cal-nav-btn" onClick={goBack}><i className="ti ti-chevron-left" /></button>
              <button className="cal-nav-btn" onClick={goForward}><i className="ti ti-chevron-right" /></button>
            </div>
            <span className="cal-month-label">{headerLabel()}</span>
            <button className="cal-today-btn" onClick={goToday}>Today</button>
            {/* Legend */}
            <div className="cal-legend">
              {LEGEND.map(l => (
                <span key={l.label} className="cal-legend-item">
                  <span className="cal-legend-dot" style={{ background: l.color }} />
                  {l.label}
                </span>
              ))}
            </div>
            {/* View buttons */}
            <div className="cal-view-btns">
              {(['month','timeline'] as ViewMode[]).map(v => (
                <button
                  key={v}
                  className={`cal-view-btn${viewMode === v ? ' active' : ''}`}
                  onClick={() => setViewMode(v)}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* ── Calendar body ── */}
          {loading ? (
            <div className="bl-loading" style={{ margin: 40 }}>
              <LoaderSymbol size={16} /> Loading events…
            </div>
          ) : (

            <>
              {/* MONTH VIEW */}
              {viewMode === 'month' && (
                <div className="sc-month">
                  {/* Day-of-week header */}
                  <div className="sc-month-hdr">
                    {DAYS.map(d => <div key={d} className="sc-dow">{d}</div>)}
                  </div>
                  {/* Grid */}
                  <div className="sc-month-grid">
                    {monthGrid.flat().map((date, i) => {
                      const key = toYMD(date);
                      const evs = eventsByDate.get(key) ?? [];
                      return (
                        <DayCell
                          key={i}
                          date={date}
                          isCurrentMonth={date.getMonth() === anchor.getMonth()}
                          isToday={sameDay(date, today)}
                          events={evs}
                          selectedId={selected?.reminderId ?? null}
                          onSelect={selectEvent}
                          onShowMore={(d, e) => setModal({ date: d, events: e })}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* TIMELINE VIEW */}
              {viewMode === 'timeline' && (
                <TimelineView eventsByDate={eventsByDate} selectedId={selected?.reminderId ?? null} onSelect={selectEvent} />
              )}
            </>
          )}
        </div>
      </div>

      {/* New / Edit Reminder Activity dialog */}
      {dialog && (
        <ReminderDialog
          mode={dialog}
          initial={dialog === 'edit' ? selected : null}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); setSelected(null); void loadEvents(); }}
        />
      )}

      {/* Day modal */}
      {modal && (
        <DayModal date={modal.date} events={modal.events} onClose={() => setModal(null)} />
      )}

      <style>{`
        /* Structural greys via theme tokens (BO UI Standard); the event colour
           palette (LEGEND / eventColor) is token-exempt data-series colour.
           The old page-local .bl-pill.ok fork was removed 2026-07-02 so the
           shared backlog.css pill wins. */

        /* ── Toolbar ── */
        .cal-toolbar {
          display:flex; align-items:center; gap:8px; flex-wrap:wrap;
          padding:10px 16px; border-bottom:1px solid var(--ai-line);
          background:#fff; flex-shrink:0;
        }
        .cal-nav { display:flex; gap:3px; }
        .cal-nav-btn {
          width:28px;height:28px;border:1px solid var(--ai-line);border-radius:5px;
          background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;
          font-size:13px;color:var(--ai-muted);transition:background .14s;
        }
        .cal-nav-btn:hover { background:var(--ai-row-hover); }
        .cal-month-label { font-size:15px;font-weight:700;color:var(--ai-ink);min-width:120px; }
        .cal-today-btn {
          padding:4px 12px;border:1px solid var(--ai-line);border-radius:5px;
          font-size:12px;background:#fff;color:var(--ai-ink);cursor:pointer;font-weight:500;
          transition:background .14s;
        }
        .cal-today-btn:hover { background:var(--ai-row-hover); }

        /* Legend */
        .cal-legend { display:flex;align-items:center;gap:10px;font-size:10px;color:var(--ai-muted);margin-left:4px; }
        .cal-legend-item { display:flex;align-items:center;gap:3px;white-space:nowrap; }
        .cal-legend-dot { display:inline-block;width:9px;height:9px;border-radius:2px; }

        /* View buttons */
        .cal-view-btns { display:flex;gap:2px;margin-left:auto; }
        .cal-view-btn {
          padding:4px 11px;border:1px solid var(--ai-line);background:#fff;
          font-size:12px;color:var(--ai-muted);cursor:pointer;font-weight:500;
          transition:background .14s;
        }
        .cal-view-btn:first-child { border-radius:5px 0 0 5px; }
        .cal-view-btn:last-child  { border-radius:0 5px 5px 0; }
        .cal-view-btn.active {
          background:var(--brand-accent, var(--ai-orange));color:#fff;border-color:var(--brand-accent, var(--ai-orange));font-weight:700;
        }
        .cal-view-btn:hover:not(.active) { background:var(--ai-row-hover); }

        /* ── Month view ── */
        .sc-month { display:flex;flex-direction:column;flex:1;overflow:hidden; }
        .sc-month-hdr {
          display:grid;grid-template-columns:repeat(7,1fr);
          background:var(--ai-header-bg);border-bottom:1px solid var(--ai-line);
        }
        .sc-dow {
          text-align:center;padding:6px 0;font-size:11px;font-weight:600;
          color:var(--ai-muted);text-transform:uppercase;letter-spacing:.04em;
        }
        .sc-month-grid {
          display:grid;grid-template-columns:repeat(7,1fr);
          grid-template-rows:repeat(6,1fr);
          flex:1;overflow-y:auto;
        }
        .sc-day {
          border-right:1px solid var(--ai-line-soft);border-bottom:1px solid var(--ai-line-soft);
          padding:4px 5px;min-height:90px;background:#fff;
          display:flex;flex-direction:column;
        }
        .sc-day:nth-child(7n) { border-right:none; }
        .sc-other-month { background:var(--ai-header-bg); }
        .sc-other-month .sc-day-num { color:var(--ai-muted); opacity:.5; }
        .sc-today { background:var(--ai-orange-tint, #fff8f2); }
        .sc-today .sc-day-num {
          background:var(--brand-accent, var(--ai-orange));color:#fff;border-radius:50%;
          width:22px;height:22px;display:flex;align-items:center;justify-content:center;
          font-weight:700;
        }
        .sc-day-num {
          font-size:12px;font-weight:600;color:var(--ai-ink);margin-bottom:3px;
          width:22px;height:22px;display:flex;align-items:center;justify-content:center;
        }
        .sc-day-events { display:flex;flex-direction:column;gap:2px;flex:1; }

        /* Event chip — clickable to select the reminder (enables Edit/Delete) */
        .sc-event-chip {
          display:flex;align-items:center;justify-content:space-between;
          padding:1px 5px;border-radius:3px;font-size:10px;color:#fff;
          font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
          cursor:pointer;box-sizing:border-box;border:2px solid transparent;
        }
        .sc-event-chip-selected {
          border-color:var(--ai-ink);box-shadow:0 0 0 1px #fff inset;
        }
        .sc-event-dot {
          width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,.8);
          flex-shrink:0;margin-left:3px;
        }
        .sc-show-more {
          font-size:10px;color:var(--ai-blue);background:none;border:none;
          cursor:pointer;padding:0;text-align:left;
          text-decoration:underline;
        }

        /* ── Week / work-week view ── */
        .sc-week { display:flex;flex-direction:column;flex:1;overflow:hidden; }
        .sc-week-hdr {
          display:grid;background:var(--ai-header-bg);border-bottom:1px solid var(--ai-line);
          grid-template-columns:repeat(var(--wk-cols,7),1fr);
        }
        .sc-week-col-hdr {
          text-align:center;padding:8px 4px;border-right:1px solid var(--ai-line-soft);
        }
        .sc-week-col-hdr:last-child { border-right:none; }
        .sc-today-hdr { background:var(--ai-orange-tint, #fff4ec); }
        /* ── Timeline view ── */
        .sc-timeline { padding:16px 20px;flex:1;overflow-y:auto; }
        .sc-tl-group { margin-bottom:20px; }
        .sc-tl-date {
          font-size:13px;font-weight:700;color:var(--ai-ink);margin-bottom:6px;
          display:flex;align-items:center;gap:8px;
        }
        .sc-tl-badge {
          background:var(--brand-accent, var(--ai-orange));color:#fff;border-radius:10px;
          padding:1px 7px;font-size:11px;font-weight:600;
        }
        .sc-tl-events { display:flex;flex-direction:column;gap:4px; }
        .sc-tl-row {
          display:flex;align-items:center;gap:8px;cursor:pointer;
          padding:5px 10px;background:#fff;border:1px solid var(--ai-line-soft);border-radius:5px;
        }
        .sc-tl-row-selected { border-color:var(--brand-accent, var(--ai-orange));background:var(--ai-orange-tint, #fff8f2); }
        .sc-tl-dot { width:10px;height:10px;border-radius:50%;flex-shrink:0; }
        .sc-tl-name { font-size:13px;font-weight:600;color:var(--ai-ink);min-width:130px; }
        .sc-tl-meta { font-size:11px;color:var(--ai-muted);flex:1; }

        /* ── Day modal ── */
        .sc-modal-backdrop {
          position:fixed;inset:0;background:rgba(0,0,0,.4);
          display:flex;align-items:center;justify-content:center;z-index:9999;
        }
        .sc-modal {
          background:#fff;border-radius:8px;width:480px;max-width:95vw;
          max-height:80vh;display:flex;flex-direction:column;
          box-shadow:0 8px 32px rgba(0,0,0,.2);
        }
        .sc-modal-wide { width:680px; }
        .sc-modal-hdr {
          display:flex;align-items:center;gap:8px;padding:12px 16px;
          border-bottom:1px solid var(--ai-line);font-weight:700;font-size:14px;
          background:var(--ai-header-bg);border-radius:8px 8px 0 0;
        }
        .sc-modal-sub {
          font-size:12px;color:var(--ai-muted);padding:6px 16px 4px;
          border-bottom:1px solid var(--ai-line-soft);background:var(--ai-header-bg);
        }
        .sc-modal-search {
          display:flex;align-items:center;gap:5px;
          border:1px solid var(--brand-accent, var(--ai-orange));border-radius:4px;padding:3px 8px;
          background:#fff;
        }
        .sc-modal-search input {
          border:none;outline:none;font-size:12px;width:160px;color:var(--ai-ink);
        }
        .sc-modal-search i { color:var(--brand-accent, var(--ai-orange));font-size:13px; }
        .sc-modal-close {
          width:26px;height:26px;border:1px solid var(--ai-line);border-radius:4px;
          background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;
        }
        .sc-modal-body { overflow-y:auto;flex:1;padding:4px 0; }
        .sc-modal-row {
          display:flex;align-items:flex-start;gap:10px;
          padding:9px 16px;border-bottom:1px solid var(--ai-line-soft);
        }
        .sc-modal-row:last-child { border-bottom:none; }
        .sc-modal-row-selected { background:var(--ai-orange-tint, #fff8f2); }
        .sc-modal-dot {
          width:10px;height:10px;border-radius:50%;
          margin-top:3px;flex-shrink:0;
        }
        .sc-modal-info { flex:1; }
        .sc-modal-name { font-size:13px;font-weight:600;color:var(--ai-ink); }
        .sc-modal-meta { font-size:11px;color:var(--ai-muted);margin-top:2px; }
        .sc-modal-footer {
          display:flex;align-items:center;gap:10px;
          padding:8px 16px;border-top:1px solid var(--ai-line);
          background:var(--ai-header-bg);border-radius:0 0 8px 8px;font-size:12px;
        }
        .sc-modal-pg-info { color:var(--ai-muted); }
        .sc-modal-count {
          background:var(--ai-pill-warn-bg);color:var(--ai-pill-warn-fg);
          border-radius:10px;padding:1px 8px;font-size:11px;font-weight:600;
        }
      `}</style>
    </div>
  );
}
